import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  View,
  ScrollView,
  TouchableOpacity,
  Modal as RNModal,
  TouchableWithoutFeedback,
  useWindowDimensions,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { Checkbox } from "@/components/ui/Checkbox";
import { AppTextArea } from "@/components/ui/AppTextArea";
import { SuccessState } from "@/components/ui/SuccessState";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import type { ApplicationDetail } from "@/lib/api/review";
import { cn } from "@/lib/utils";
import { ErrorState } from "@/components/ui/ErrorState";
import { SearchInput } from "@/components/ui/SearchInput";
import { KeyboardAvoidingView } from "@/components/ui/KeyboardAvoidingView";
import { useI18n } from "@/context/I18nContext";

// ─── Field definitions ────────────────────────────────────────────────────────

type FieldValueType = "text" | "file" | "files";

interface ChangeRequestField {
  key: string;
  backendKey: string;
  sectionKey: TranslationKeys;
  labelKey: TranslationKeys;
  valueType: FieldValueType;
  getValue: (app: ApplicationDetail) => string | string[] | null;
}

const FIELDS: ChangeRequestField[] = [
  // Legal Representative
  {
    key: "id_type",
    backendKey: "step1.legal_representative_id_document",
    sectionKey: "review.application.subtabs.legalRepresentative",
    labelKey: "review.review.decision.requestChangesModal.fields.legalRepresentativeId",
    valueType: "file",
    getValue: (app) => app.legalRepresentative?.idType ?? null,
  },
  // Business Information
  {
    key: "companyNameEnglish",
    backendKey: "step2.company_name_english",
    sectionKey: "review.application.subtabs.businessInformation",
    labelKey: "review.review.decision.requestChangesModal.fields.companyNameEnglish",
    valueType: "text",
    getValue: (app) => app.businessInformation?.companyNameEnglish ?? null,
  },
  {
    key: "ussc",
    backendKey: "step2.unified_social_credit_code",
    sectionKey: "review.application.subtabs.businessInformation",
    labelKey: "review.review.decision.requestChangesModal.fields.unifiedSocialCreditCode",
    valueType: "text",
    getValue: (app) => app.businessInformation?.ussc ?? null,
  },
  {
    key: "businessLicense",
    backendKey: "step2.business_license_document",
    sectionKey: "review.application.subtabs.businessInformation",
    labelKey: "review.review.decision.requestChangesModal.fields.businessLicense",
    valueType: "file",
    getValue: (app) =>
      app.businessInformation?.businessLicenseUrl
        ? (app.businessInformation.businessLicenseUrl.split("/").pop() ?? null)
        : null,
  },
  // Product & Compliance
  {
    key: "operatingIndustries",
    backendKey: "step3.industry",
    sectionKey: "review.application.subtabs.productCompliance",
    labelKey: "review.review.decision.requestChangesModal.fields.operatingIndustries",
    valueType: "text",
    getValue: (app) => app.productCompliance?.operatingIndustries?.join(", ") ?? null,
  },
  {
    key: "operatingCategories",
    backendKey: "step3.product_subcategories",
    sectionKey: "review.application.subtabs.productCompliance",
    labelKey: "review.review.decision.requestChangesModal.fields.operatingCategories",
    valueType: "text",
    getValue: (app) => app.productCompliance?.operatingCategories?.join(", ") ?? null,
  },
  {
    key: "isoCertificate",
    backendKey: "step3.iso_certification_document",
    sectionKey: "review.application.subtabs.productCompliance",
    labelKey: "review.review.decision.requestChangesModal.fields.isoCertificate",
    valueType: "file",
    getValue: (app) => app.productCompliance?.isoCertificate?.fileName ?? null,
  },
  {
    key: "ceMarking",
    backendKey: "step3.ce_marking_document",
    sectionKey: "review.application.subtabs.productCompliance",
    labelKey: "review.review.decision.requestChangesModal.fields.ceMarking",
    valueType: "file",
    getValue: (app) => app.productCompliance?.ceMarking?.fileName ?? null,
  },
  {
    key: "ecoCertifications",
    backendKey: "step3.eco_certification_document",
    sectionKey: "review.application.subtabs.productCompliance",
    labelKey: "review.review.decision.requestChangesModal.fields.ecoCertifications",
    valueType: "files",
    getValue: (app) => app.productCompliance?.ecoCertifications?.map((c) => c.fileName) ?? null,
  },
  // Financial Settlement
  {
    key: "alipayMerchantId",
    backendKey: "step4.alipay_merchant_id",
    sectionKey: "review.application.subtabs.financialSettlement",
    labelKey: "review.review.decision.requestChangesModal.fields.alipayMerchantId",
    valueType: "text",
    getValue: (app) => app.financialSettlement?.alipay?.merchantId ?? null,
  },
  {
    key: "wechatMerchantId",
    backendKey: "step4.wechatpay_merchant_id",
    sectionKey: "review.application.subtabs.financialSettlement",
    labelKey: "review.review.decision.requestChangesModal.fields.wechatMerchantId",
    valueType: "text",
    getValue: (app) => app.financialSettlement?.wechatPay?.merchantId ?? null,
  },
  // Branding & Localization
  {
    key: "shortNameEnglish",
    backendKey: "step5.business_short_name_english",
    sectionKey: "review.application.subtabs.brandingLocalization",
    labelKey: "review.review.decision.requestChangesModal.fields.shortNameEnglish",
    valueType: "text",
    getValue: (app) => app.brandingLocalization?.shortNameEnglish ?? null,
  },
  {
    key: "descriptionEnglish",
    backendKey: "step5.business_description_english",
    sectionKey: "review.application.subtabs.brandingLocalization",
    labelKey: "review.review.decision.requestChangesModal.fields.descriptionEnglish",
    valueType: "text",
    getValue: (app) => app.brandingLocalization?.descriptionEnglish ?? null,
  },
  {
    key: "businessLogo",
    backendKey: "step5.business_logo",
    sectionKey: "review.application.subtabs.brandingLocalization",
    labelKey: "review.review.decision.requestChangesModal.fields.businessLogo",
    valueType: "file",
    getValue: (app) =>
      app.brandingLocalization?.logoUrl
        ? (app.brandingLocalization.logoUrl.split("/").pop() ?? null)
        : null,
  },
];

// Unique section keys in order
const SECTION_KEYS: TranslationKeys[] = FIELDS.reduce<TranslationKeys[]>(
  (acc, f) => (acc.includes(f.sectionKey) ? acc : [...acc, f.sectionKey]),
  []
);

// ─── Sub-components ───────────────────────────────────────────────────────────

function FileValue({ fileName }: { fileName: string }) {
  return (
    <View className="flex-row items-center gap-2">
      <Icon as={Feather} name="file" size={16} className="text-supporting-text-40" />
      <AppText className="text-base text-supporting-text-40 flex-1" numberOfLines={1}>
        {fileName}
      </AppText>
    </View>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface RequestChangesModalProps {
  visible: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
  application: ApplicationDetail | undefined;
  isSubmitting?: boolean;
  onSubmit: (requestedChanges: { field_key: string; reason: string }[]) => Promise<void>;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
  /** Field keys (UI keys) that are already rejected — pre-selected when the modal opens. */
  rejectedFieldKeys?: string[];
  /** Rejection reasons keyed by UI field key — pre-fills the reason inputs. */
  rejectedReasons?: Record<string, string>;
}

type ModalStep = "step1" | "step2" | "success" | "error";

// ─── Component ───────────────────────────────────────────────────────────────

export function RequestChangesModal({
  visible,
  onDismiss,
  onConfirm,
  application,
  isSubmitting,
  onSubmit,
  t,
  rejectedFieldKeys,
  rejectedReasons,
}: RequestChangesModalProps) {
  const { isRTL } = useI18n();
  const isWideScreen = useIsWideScreen();
  const { height: windowHeight } = useWindowDimensions();

  const [step, setStep] = useState<ModalStep>("step1");
  const [search, setSearch] = useState("");
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [errorKey, setErrorKey] = useState<TranslationKeys | null>(null);

  const resetState = useCallback(() => {
    setStep("step1");
    setSearch("");
    setSelectedKeys(new Set());
    setReasons({});
  }, []);

  // Pre-populate selections and reasons from already-rejected fields on open (false→true transition).
  const prevVisibleRef = useRef(false);
  useEffect(() => {
    if (visible && !prevVisibleRef.current) {
      if (rejectedFieldKeys?.length) setSelectedKeys(new Set(rejectedFieldKeys));
      if (rejectedReasons) setReasons(rejectedReasons);
    }
    prevVisibleRef.current = visible;
  }, [visible, rejectedFieldKeys, rejectedReasons]);

  const handleDismiss = useCallback(() => {
    resetState();
    onDismiss();
    if (step === "success") onConfirm();
  }, [resetState, onDismiss, onConfirm, step]);

  // Derived – field list with values resolved from application
  const fields = useMemo(() => {
    return FIELDS.map((f) => ({
      ...f,
      resolvedValue: application ? f.getValue(application) : null,
    }));
  }, [application]);

  // Filter by search query
  const filteredFields = useMemo(() => {
    if (!search.trim()) return fields;
    const q = search.toLowerCase();
    return fields.filter(
      (f) => t(f.labelKey).toLowerCase().includes(q) || t(f.sectionKey).toLowerCase().includes(q)
    );
  }, [fields, search, t]);

  // Toggle a field selection
  const toggleField = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Remove a selected field
  const removeField = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setReasons((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const selectedFields = useMemo(
    () => fields.filter((f) => selectedKeys.has(f.key)),
    [fields, selectedKeys]
  );

  const handleSubmit = useCallback(async () => {
    setErrorKey(null);
    const requested_changes = selectedFields.map((f) => ({
      field_key: f.backendKey,
      reason: reasons[f.key]?.trim() || "",
    }));
    try {
      await onSubmit(requested_changes);
      setStep("success");
    } catch {
      setErrorKey("common.error.generic");
      setStep("error");
    }
  }, [selectedFields, reasons, onSubmit]);

  // ── Step 1 field list ──────────────────────────────────────────────────────

  function renderFieldRow(field: (typeof fields)[number], isLast: boolean) {
    const checked = selectedKeys.has(field.key);
    const value = field.resolvedValue;

    return (
      <View key={field.key} className="w-full">
        <TouchableOpacity
          onPress={() => toggleField(field.key)}
          activeOpacity={0.7}
          className="flex-row items-start gap-4 py-3"
          accessibilityRole="checkbox"
          accessibilityState={{ checked }}
        >
          {/* Checkbox */}
          <View className="pt-0.5">
            <Checkbox checked={checked} onChange={() => toggleField(field.key)} variant="default" />
          </View>

          {/* Field info */}
          <View className="flex-1 items-start gap-2">
            <AppText weight="medium" className="text-base text-supporting-text-50">
              {t(field.labelKey)}
            </AppText>
            {value != null && (
              <View className="gap-2">
                {Array.isArray(value) ? (
                  value.map((v, i) => <FileValue key={i} fileName={v} />)
                ) : field.valueType === "file" || field.valueType === "files" ? (
                  <FileValue fileName={value} />
                ) : (
                  <AppText className="text-base text-supporting-text-40" numberOfLines={2}>
                    {value}
                  </AppText>
                )}
              </View>
            )}
          </View>
        </TouchableOpacity>

        {!isLast && <View className="h-px w-full bg-stroke-40" />}
      </View>
    );
  }

  function renderStep1FieldList() {
    const filteredSections = SECTION_KEYS.map((sectionKey) => {
      const sectionFields = filteredFields.filter((f) => f.sectionKey === sectionKey);
      return { sectionKey, sectionFields };
    }).filter((s) => s.sectionFields.length > 0);

    if (filteredSections.length === 0) {
      return (
        <View className="py-8 items-center justify-center">
          <AppText className="text-base text-dark-text-10 text-center">
            {t("review.review.decision.requestChangesModal.noFieldsFound")}
          </AppText>
        </View>
      );
    }

    return filteredSections.map(({ sectionKey, sectionFields }, si) => (
      <View key={sectionKey} className={cn("items-start gap-2", si > 0 && "mt-10")}>
        {/* Section header */}
        <AppText weight="semibold" className="text-sm text-dark-text-20 tracking-[0.56px]">
          {t(sectionKey).toUpperCase()}
        </AppText>

        {/* Fields */}
        <View className="px-4 w-full items-start">
          {sectionFields.map((field, fi) => renderFieldRow(field, fi === sectionFields.length - 1))}
        </View>
      </View>
    ));
  }

  // ── Step 1 right panel ────────────────────────────────────────────────────

  function renderSelectedPanel() {
    return (
      <View className="py-4 gap-4 self-stretch items-start">
        {/* Header */}
        <View className="px-4 gap-1 items-start">
          <AppText weight="semibold" className="text-[18px] text-dark-text-50">
            {t("review.review.decision.requestChangesModal.fieldsSelected")}
          </AppText>
          <View className="flex-row items-center gap-1">
            <Icon as={Feather} name="info" size={14} className="text-dark-text-10" />
            <AppText className="text-sm text-dark-text-10">
              {t("review.review.decision.requestChangesModal.youHaveSelectedCount", {
                count: String(selectedKeys.size),
              })}
            </AppText>
          </View>
        </View>

        {selectedKeys.size === 0 ? (
          <View className="px-4 py-4">
            <AppText className="text-sm text-dark-text-10 italic">
              {t("review.review.decision.requestChangesModal.noFieldsSelectedHint")}
            </AppText>
          </View>
        ) : (
          <View className="w-full items-start">
            {selectedFields.map((field, i) => (
              <View key={field.key} className="w-full">
                <View className="flex-row items-center justify-between px-4 py-2 gap-2">
                  <AppText weight="medium" className="text-base text-supporting-text-40">
                    {t(field.labelKey)}
                  </AppText>
                  <TouchableOpacity
                    onPress={() => removeField(field.key)}
                    className="flex-row items-center gap-1"
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  >
                    <Icon as={Feather} name="x" size={14} className="text-error" />
                    <AppText weight="medium" className="text-sm text-error">
                      {t("review.review.decision.requestChangesModal.remove")}
                    </AppText>
                  </TouchableOpacity>
                </View>
                {i < selectedFields.length - 1 && <View className="h-px w-full bg-stroke-50" />}
              </View>
            ))}
          </View>
        )}
      </View>
    );
  }

  // ── Step 2 ─────────────────────────────────────────────────────────────────

  function renderStep2() {
    return (
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerClassName="gap-6 p-4 flex-grow-1"
        className="bg-white"
      >
        {selectedFields.map((field) => (
          <View key={field.key} className="bg-stroke-10 rounded-lg p-4 gap-3 items-start">
            <AppText weight="semibold" className="text-base text-dark-text-50">
              {t(field.labelKey)}
            </AppText>
            <AppTextArea
              label="review.review.decision.requestChangesModal.addReason"
              placeholder="review.review.decision.requestChangesModal.reasonPlaceholder"
              value={reasons[field.key] ?? ""}
              onChangeText={(text) => setReasons((prev) => ({ ...prev, [field.key]: text }))}
              numberOfLines={3}
              required
              containerClassName="mb-0"
            />
          </View>
        ))}
      </ScrollView>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <RNModal
      visible={visible}
      transparent
      animationType="fade"
      statusBarTranslucent
      onRequestClose={handleDismiss}
    >
      <TouchableWithoutFeedback accessible={false}>
        <View className="flex-1 bg-black/50 items-center justify-center px-4 py-8">
          <KeyboardAvoidingView
            behavior={step === "step2" ? "translate-with-padding" : undefined}
            className="w-full items-center"
          >
            <TouchableWithoutFeedback accessible={false}>
              <View
                className={cn(
                  "bg-white rounded-xl w-full overflow-hidden shadow-xl max-w-4xl lg:max-w-6xl",
                  (step === "success" || step === "error") && "max-h-[360px] max-w-lg"
                )}
                style={step === "step1" || step === "step2" ? { height: windowHeight * 0.85 } : {}}
                aria-modal
                accessible
                accessibilityViewIsModal
              >
                {/* ── Step 1 & 2: header + body + footer ── */}

                <>
                  {/* Header */}
                  <View
                    className={cn(
                      (step === "step1" || step === "step2") && "border-b border-stroke-50",
                      "flex-row items-start justify-between px-4 lg:px-6 py-4 lg:py-5"
                    )}
                  >
                    <View className="gap-0.5 flex-1 pe-4 items-start">
                      {(step === "step1" || step === "step2") && (
                        <>
                          <AppText weight="semibold" className="text-lg text-dark-text-50">
                            {t("review.review.decision.requestChangesModal.title")}
                          </AppText>
                          <AppText weight="medium" className="text-sm text-supporting-text">
                            {step === "step1"
                              ? t("review.review.decision.requestChangesModal.step1Subtitle")
                              : t("review.review.decision.requestChangesModal.step2Subtitle")}
                          </AppText>
                        </>
                      )}
                    </View>
                    <TouchableOpacity
                      onPress={handleDismiss}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                      accessibilityRole="button"
                    >
                      <Icon as={Feather} name="x" size={20} className="text-dark-text-50" />
                    </TouchableOpacity>
                  </View>

                  {(step === "step1" || step === "step2") && (
                    <>
                      {/* Body */}
                      <View className="p-4 lg:p-6 flex-1">
                        <View className="flex-1 overflow-hidden border border-stroke-50 rounded-lg">
                          {step === "step1" && (
                            <View
                              className={cn(
                                "flex-1 overflow-hidden",
                                isWideScreen ? "flex-row" : "flex-col"
                              )}
                            >
                              {/* Left panel */}
                              <View
                                className={cn(
                                  "flex-1",
                                  isWideScreen
                                    ? "border-r border-stroke-50"
                                    : "border-b border-stroke-50"
                                )}
                              >
                                <View className="px-4 pt-4 pb-3">
                                  <SearchInput
                                    value={search}
                                    onChangeText={setSearch}
                                    placeholder="common.search"
                                  />
                                </View>
                                <ScrollView
                                  showsVerticalScrollIndicator={false}
                                  contentContainerClassName="px-4 pb-4"
                                  style={{ flex: 1 }}
                                >
                                  {renderStep1FieldList()}
                                </ScrollView>
                              </View>

                              {/* Right panel — fixed sidebar (wide screens only) */}
                              {isWideScreen && (
                                <ScrollView
                                  showsVerticalScrollIndicator={false}
                                  className="bg-stroke-10 max-w-80"
                                >
                                  {renderSelectedPanel()}
                                </ScrollView>
                              )}

                              {/* Selected fields below (narrow screens) */}
                              {!isWideScreen && selectedKeys.size > 0 && (
                                <ScrollView
                                  showsVerticalScrollIndicator={false}
                                  className="bg-stroke-10"
                                  style={{ maxHeight: 220 }}
                                >
                                  {renderSelectedPanel()}
                                </ScrollView>
                              )}
                            </View>
                          )}

                          {step === "step2" && renderStep2()}
                        </View>
                      </View>

                      {/* Footer */}
                      <View className="flex-row items-center justify-end gap-4 px-4 lg:px-6 py-4 lg:py-5 border-t border-stroke-50">
                        <Button
                          label={
                            step === "step1"
                              ? "common.cancel"
                              : "review.review.decision.requestChangesModal.back"
                          }
                          variant="tertiary"
                          icon={
                            step === "step2" ? (
                              <Icon
                                as={Feather}
                                name={isRTL ? "arrow-right" : "arrow-left"}
                                size={16}
                                className="text-primary"
                              />
                            ) : undefined
                          }
                          onPress={step === "step1" ? handleDismiss : () => setStep("step1")}
                        />
                        <Button
                          label={
                            step === "step1"
                              ? "review.review.decision.requestChangesModal.addReasons"
                              : "review.review.decision.requestChangesModal.sendChangeRequest"
                          }
                          variant="primary"
                          disabled={
                            step === "step1"
                              ? selectedKeys.size === 0
                              : selectedFields.some((f) => !reasons[f.key]?.trim()) || isSubmitting
                          }
                          loading={step === "step2" && isSubmitting}
                          onPress={step === "step1" ? () => setStep("step2") : handleSubmit}
                          trailingIcon={
                            <Icon
                              as={Feather}
                              name={isRTL ? "arrow-left" : "arrow-right"}
                              size={16}
                              className="text-white"
                            />
                          }
                        />
                      </View>
                    </>
                  )}
                </>

                {/* ── Success state ── */}
                {step === "success" && (
                  <View className="p-6 gap-4 h-full">
                    <SuccessState
                      title="review.review.decision.requestChangesModal.successTitle"
                      description="review.review.decision.requestChangesModal.successDescription"
                      className="pt-0 mb-12"
                    />
                  </View>
                )}

                {/* ── Error state ── */}
                {step === "error" && (
                  <View className="p-6 gap-4 h-full">
                    <ErrorState
                      description={errorKey || "common.error.generic"}
                      onRetry={() => {
                        setStep("step2");
                        setErrorKey(null);
                      }}
                      className="pt-0 mb-12"
                    />
                  </View>
                )}
              </View>
            </TouchableWithoutFeedback>
          </KeyboardAvoidingView>
        </View>
      </TouchableWithoutFeedback>
    </RNModal>
  );
}
